import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import { getStorage, ref as storageRef, getDownloadURL, uploadBytes } from 'firebase/storage';

function DomainPage() {
  const { id } = useParams();
  const storage = getStorage();
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const filePath = id ? `domain/${id}.md` : null;

  const loadContent = useCallback(async () => {
    if (!filePath) return;
    setIsLoading(true);
    setStatusMessage('Loading...');

    try {
      const fileRef = storageRef(storage, filePath);
      const url = await getDownloadURL(fileRef);
      const response = await fetch(url);
      const text = await response.text();
      setContent(text);
      setStatusMessage('Loaded');
    } catch (error) {
      if (error?.code === 'storage/object-not-found') {
        setContent('');
        setStatusMessage('No existing content. Start writing your plan.');
      } else {
        console.error('Failed to load domain doc:', error);
        setStatusMessage('Failed to load content.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [filePath, storage]);

  useEffect(() => {
    if (!filePath) return;
    loadContent();
  }, [filePath, loadContent]);

  const handleSave = async () => {
    if (!filePath) return;
    setIsSaving(true);
    setStatusMessage('Saving...');
    try {
      const blob = new Blob([content], { type: 'text/markdown' });
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, blob);
      setStatusMessage('Saved');
    } catch (error) {
      console.error('Failed to save domain doc:', error);
      setStatusMessage('Failed to save.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!id) {
    return (
      <div style={containerStyle}>
        <h2>Missing domain id</h2>
        <p>Please provide a valid domain identifier in the URL.</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Domain Note: {id}</h2>
          <p style={{ margin: '4px 0 0 0', color: '#AAA', fontSize: '0.9rem' }}>
            Storage path: {filePath}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007BFF',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <span style={{ color: '#AAA', fontSize: '0.9rem' }}>{statusMessage}</span>
        </div>
      </header>

      <div style={{ flex: 1 }}>
        <TextareaAutosize
          value={content}
          onChange={(e) => setContent(e.target.value)}
          minRows={20}
          style={{
            width: '100%',
            backgroundColor: '#1F1F1F',
            color: '#FFFFFF',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '16px',
            fontFamily: 'monospace',
            fontSize: '1rem',
            resize: 'vertical',
            lineHeight: 1.5,
          }}
          disabled={isLoading}
          placeholder={
            isLoading
              ? 'Loading domain content...'
              : 'Start writing your domain plan here...'
          }
        />
      </div>
    </div>
  );
}

const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#121212',
  color: '#FFFFFF',
  padding: '24px',
  boxSizing: 'border-box',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  flexWrap: 'wrap',
  gap: '12px',
};

export default DomainPage;
